/**
 * Shared state hub for all UI modules.
 * Single source of truth for CPU instance, assembly results, theme colors, and utilities.
 */

import {
    CPU,
    IO_START,
    IO_DISPLAY_END,
    IO_TX_DATA,
    IO_TX_STATUS,
    IO_RX_DATA,
    IO_RX_STATUS,
    SP_INIT,
    PAGE_SIZE,
} from "../lib/core.js";

// ── CPU instance ────────────────────────────────────────────────

export const cpu = new CPU({ arch: 3 });

// ── Constants ───────────────────────────────────────────────────

export const STACK_BASE = SP_INIT - 2;
export const IO_BASE = IO_START;
export { PAGE_SIZE, IO_DISPLAY_END, IO_TX_DATA, IO_TX_STATUS, IO_RX_DATA, IO_RX_STATUS };

// ── Pad state (mutated by pad.js) ──────────────────────────────

export const pad = {
    visible: false,
    start: 0, // base address
    end: 0, // base address + size
};

// ── Assembly state (mutated by orchestrator) ────────────────────

export const asm = {
    labels: {}, // label name → address
    mapping: {}, // address → source line (1-based, flat)
    lineMap: null, // Map<flatLine, {file: string|null, line: number}>
    instrStarts: new Set(),
    labelAddrs: new Set(), // absolute addresses that are label entry points (globals only)
    labelNames: new Map(), // absolute address → label name (globals only)
    lastCode: null,
    lastUsedBytes: 0,
};

// ── CSS helper ──────────────────────────────────────────────────

export function cssVar(name) {
    return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

// ── Theme colors (mutated in-place on theme switch) ─────────────

export const colors = {};
export const regColors = {};

export function refreshColors() {
    if (typeof getComputedStyle === "undefined") return;
    Object.assign(colors, {
        or: cssVar("--a-orange"),
        bl: cssVar("--a-blue"),
        gr: cssVar("--a-green"),
        rd: cssVar("--a-red"),
        yl: cssVar("--a-yellow"),
        pu: cssVar("--a-purple"),
        dim: cssVar("--t-dim"),
        mid: cssVar("--t-mid"),
        txt: cssVar("--t-text"),
        canvas: cssVar("--t-canvas"),
    });
    Object.assign(regColors, {
        A: colors.gr,
        B: colors.bl,
        C: colors.or,
        D: colors.rd,
        IP: colors.or,
        SP: colors.yl,
        DP: colors.mid,
    });
}
refreshColors();

// ── Formatting utilities ────────────────────────────────────────

export function hex(v, p = 2) {
    return v.toString(16).toUpperCase().padStart(p, "0");
}

export function printableChar(v) {
    return v >= 32 && v < 127 ? String.fromCharCode(v) : "";
}

/**
 * Wire a format-toggle toolbar: click on [data-{attr}] → update state, toggle .act, re-render.
 * @returns {{ get(): string }} accessor for the current format value
 */
export function initFormatToggle(blockId, tabsSelector, attr, renderFn, initial = "hex") {
    const state = { fmt: initial };
    document.getElementById(blockId).addEventListener("click", (e) => {
        const t = e.target.closest(`[data-${attr}]`);
        if (!t) return;
        state.fmt = t.dataset[attr];
        document
            .querySelectorAll(`${tabsSelector} .ft`)
            .forEach((b) => b.classList.toggle("act", b.dataset[attr] === state.fmt));
        renderFn();
    });
    return { get: () => state.fmt };
}

/** FP status register flag layout — shared by FPU (FPSR) and VU (VFPSR). */
const FPSR_FLAGS = [
    { n: "NV", bit: 0 },
    { n: "DZ", bit: 1 },
    { n: "OF", bit: 2 },
    { n: "UF", bit: 3 },
    { n: "NX", bit: 4 },
];

/** Render FPSR/VFPSR flag bits as HTML spans. onColor is used for active flags. */
export function fpsrFlagsHtml(register, onColor) {
    return FPSR_FLAGS.map((f) => {
        const on = (register >> f.bit) & 1;
        return `<span class="fb" style="font-size:8px;border-color:${on ? onColor : "var(--t-border)"};color:${on ? onColor : colors.dim};">${f.n}</span>`;
    }).join("");
}

export function escapeHtml(s) {
    return s.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;").replaceAll('"', "&quot;");
}
